import { useEffect, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import MessageDialog from '../components/MessageDialog';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { fetchSession, hasPermission } from '../lib/session';

type Role = { id: string; name: string; permissions: string[]; userCount: number };
type PermissionOption = { id: string; name: string };
type PermissionAction = { id: string; name: string; action: string };
type PermissionGroup = { resource: string; actions: PermissionAction[] };

const ACTION_ORDER = ['read', 'write'];

function splitPermissionName(name: string): { resource: string; action: string } {
  const separator = name.indexOf('.');
  if (separator <= 0 || separator === name.length - 1) {
    return { resource: name, action: name };
  }
  return { resource: name.slice(0, separator), action: name.slice(separator + 1) };
}

function compareActions(left: string, right: string): number {
  const leftIndex = ACTION_ORDER.indexOf(left);
  const rightIndex = ACTION_ORDER.indexOf(right);
  if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
  if (leftIndex === -1) return 1;
  if (rightIndex === -1) return -1;
  return leftIndex - rightIndex;
}

function groupPermissions(options: PermissionOption[]): PermissionGroup[] {
  const groups: PermissionGroup[] = [];
  const byResource = new Map<string, PermissionGroup>();
  for (const option of options) {
    const { resource, action } = splitPermissionName(option.name);
    const existing = byResource.get(resource);
    if (existing) {
      existing.actions.push({ id: option.id, name: option.name, action });
      continue;
    }
    const group = { resource, actions: [{ id: option.id, name: option.name, action }] };
    byResource.set(resource, group);
    groups.push(group);
  }
  for (const group of groups) {
    group.actions.sort((left, right) => compareActions(left.action, right.action));
  }
  return groups;
}

function isRole(value: unknown): value is Role {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === 'string'
    && typeof row.name === 'string'
    && typeof row.userCount === 'number'
    && Array.isArray(row.permissions)
    && row.permissions.every((item) => typeof item === 'string');
}

function isPermissionOption(value: unknown): value is PermissionOption {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === 'string' && typeof row.name === 'string';
}

export default function AdminRoles() {
  const [items, setItems] = useState<Role[]>([]);
  const [permissionOptions, setPermissionOptions] = useState<PermissionOption[]>([]);
  const [canWrite, setCanWrite] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ title: string; message: string } | null>(null);

  async function load() {
    const res = await fetch('/api/admin/roles');
    if (res.status === 403) {
      setItems([]);
      setPermissionOptions([]);
      return;
    }
    const data: unknown = await res.json();
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid roles response');
    }
    const payload = data as { items?: unknown; permissions?: unknown };
    setItems(Array.isArray(payload.items) ? payload.items.filter(isRole) : []);
    setPermissionOptions(Array.isArray(payload.permissions) ? payload.permissions.filter(isPermissionOption) : []);
  }

  useEffect(() => {
    (async () => {
      const session = await fetchSession();
      setCanWrite(hasPermission(session, 'roles.write'));
      await load();
    })();
  }, []);

  function openCreate() {
    setEditing(null);
    setName('');
    setSelected([]);
    setError(null);
    setOpen(true);
  }

  function openEdit(role: Role) {
    setEditing(role);
    setName(role.name);
    setSelected([...role.permissions]);
    setError(null);
    setOpen(true);
  }

  function togglePermission(permission: string, checked: boolean) {
    setSelected((current) => (
      checked ? Array.from(new Set([...current, permission])) : current.filter((item) => item !== permission)
    ));
  }

  async function save() {
    setError(null);
    const path = editing ? `/api/admin/roles/${editing.id}` : '/api/admin/roles';
    const res = await fetch(path, {
      method: editing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, permissions: selected }),
    });
    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      const message = data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : 'Save failed';
      setError(message);
      return;
    }
    setOpen(false);
    await load();
  }

  async function removeRole(role: Role) {
    const res = await fetch(`/api/admin/roles/${role.id}`, { method: 'DELETE' });
    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      const message = data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : 'Delete failed';
      setNotice({ title: 'Delete failed', message });
      return;
    }
    await load();
  }

  return (
    <div className="min-h-screen">
      <AppHeader />
      <MessageDialog notice={notice} onClose={() => setNotice(null)} />
      <div className="page-frame">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Roles</h1>
          {canWrite && <Button onClick={openCreate}>New Role</Button>}
        </div>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="p-2 text-left font-semibold">Name</th>
                <th className="p-2 text-left font-semibold">Permissions</th>
                <th className="p-2 text-left font-semibold">Users</th>
                <th className="p-2 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((role) => (
                <tr key={role.id} className="border-t">
                  <td className="p-2">{role.name}</td>
                  <td className="p-2">{role.permissions.length ? role.permissions.join(', ') : '—'}</td>
                  <td className="p-2">{role.userCount}</td>
                  <td className="p-2">
                    {canWrite && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            aria-label={`Actions for ${role.name}`}
                          >
                            <MoreHorizontal />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(role)}>Edit</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:bg-destructive/15 focus:text-destructive"
                            onClick={() => removeRole(role)}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Role' : 'Create Role'}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-3">
              <div>
                <Label htmlFor="role-name">Name</Label>
                <Input id="role-name" value={name} onChange={(event) => setName(event.target.value)} />
              </div>
              <div className="grid gap-2">
                <span className="text-sm font-medium">Permissions</span>
                {groupPermissions(permissionOptions).map((group) => (
                  <div key={group.resource} className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                    <span className="w-20 shrink-0 font-medium capitalize">{group.resource}:</span>
                    {group.actions.map((permission) => (
                      <label key={permission.id} className="flex items-center gap-2">
                        <Checkbox
                          checked={selected.includes(permission.name)}
                          onCheckedChange={(value) => togglePermission(permission.name, value === true)}
                          aria-label={`${group.resource} ${permission.action}`}
                        />
                        {permission.action}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button onClick={save}>{editing ? 'Save' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

